import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import './App.css';
import Carousel from './components/Carousel';
import ManualReviews from './components/ManualReviews';
import Coaches from './pages/Coaches';
import Gallery from './pages/Gallery';
import Schedule from './pages/Schedule';
import FAQ from './pages/FAQ';
import site from './content/site.json';
import programsData from './content/programs.json';
import pricingData from './content/pricing.json';
import theme from './content/theme.json';
import hero from './content/hero.json';
import announcement from './content/announcement.json';
import seo from './content/seo.json';

const programs = programsData.items;
const pricing = pricingData.items;

function App() {
  const logoUrl = process.env.PUBLIC_URL + '/logo-white.png';
  const SIGNUP_URL = site.signupUrl;
  const ADDRESS = site.address;
  const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(ADDRESS);
  const [navOpen, setNavOpen] = useState(false);
  const [hash, setHash] = useState(typeof window !== 'undefined' ? window.location.hash : '');
  const [path, setPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [heroImages, setHeroImages] = useState([]);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const route = useMemo(() => {
    const h = (hash || '').replace('#', '');
    const p = path || '/';
    // Support old hash routes and new clean paths
    if (h.startsWith('/coaches') || p === '/coaches') return 'coaches';
    if (h.startsWith('/schedule') || p === '/schedule') return 'schedule';
    if (h.startsWith('/gallery') || p === '/gallery') return 'gallery';
    if (h.startsWith('/faq') || p === '/faq') return 'faq';
    return 'home';
  }, [hash, path]);
  const isCoachesPage = route === 'coaches';
  const isSchedulePage = route === 'schedule';
  const isGalleryPage = route === 'gallery';
  const isFaqPage = route === 'faq';

  const closeNav = () => setNavOpen(false);
  const navigate = (to) => {
    if (typeof window === 'undefined') return;
    if (to.startsWith('#')) {
      // in-page anchors
      const id = to.replace('#', '');
      if (id === 'home') {
        window.history.pushState({}, '', '/');
        setPath('/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        if (window.location.pathname !== '/') {
          // Move to home and then apply the hash so hashchange fires
          window.history.pushState({}, '', '/');
          setPath('/');
          setTimeout(() => {
            window.location.hash = '#' + id;
          }, 0);
        } else {
          // Already on home, trigger hashchange
          window.location.hash = '#' + id;
        }
      }
      return;
    }
    // Route navigation
    window.history.pushState({}, '', to);
    setPath(to);
    if (to === '/') {
      // Clear any previous hash and scroll to top
      try {
        window.history.replaceState({}, '', '/');
      } catch {}
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Inject theme CSS variables from src/content/theme.json
  useEffect(() => {
    const r = document.documentElement;
    if (theme.accent) r.style.setProperty('--red', theme.accent);
    if (theme.black) r.style.setProperty('--black', theme.black);
    if (theme.dark) r.style.setProperty('--dark', theme.dark);
    if (theme.white) r.style.setProperty('--white', theme.white);
    if (theme.light) r.style.setProperty('--light', theme.light);
    if (theme.muted) r.style.setProperty('--muted', theme.muted);
    if (theme.radius) r.style.setProperty('--radius', theme.radius);
  }, []);

  // hCaptcha: inject script and expose callbacks
  useEffect(() => {
    window.onHCaptchaVerified = (token) => setCaptchaToken(token);
    window.onHCaptchaExpired = () => setCaptchaToken('');

    if (!document.querySelector('script[data-hcaptcha]')) {
      const s = document.createElement('script');
      s.setAttribute('data-hcaptcha', 'true');
      s.src = 'https://js.hcaptcha.com/1/api.js';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    return () => {
      delete window.onHCaptchaVerified;
      delete window.onHCaptchaExpired;
    };
  }, []);

  // Analytics: inject Plausible if configured
  useEffect(() => {
    const domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
    if (!domain) return;
    const existing = document.querySelector('script[data-plausible]');
    if (existing) return;
    const s = document.createElement('script');
    s.setAttribute('data-plausible', 'true');
    s.setAttribute('data-domain', domain);
    s.defer = true;
    s.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(s);
  }, []);

  // React to hash changes for page routing and in-page anchors
  useEffect(() => {
    const onHashChange = () => {
      const h = window.location.hash || '';
      setHash(h);
      const id = h.replace('#', '');
      // Non-page anchors only
      const sectionIds = ['home', 'programs', 'pricing', 'testimonials', 'contact'];
      if (sectionIds.includes(id)) {
        setTimeout(() => {
          if (!id || id === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }
    };
    const onPopState = () => setPath(window.location.pathname || '/');
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onPopState);
    onHashChange();
    onPopState();
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  // Scroll to top on page route change (coaches/schedule/gallery/faq)
  useEffect(() => {
    const pageRoute = isCoachesPage || isSchedulePage || isGalleryPage || isFaqPage;
    if (pageRoute) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [isCoachesPage, isSchedulePage, isGalleryPage, isFaqPage]);

  // Netlify Forms handles spam with honeypot + reCAPTCHA; no extra JS needed

  // Load hero images dynamically from hero.json if present
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const controller = new AbortController();
    fetch((process.env.PUBLIC_URL || '') + '/hero.json', { signal: controller.signal, cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j || !Array.isArray(j.items)) return;
        const list = j.items
          .map((it) => (it && typeof it.src === 'string' ? it.src.replace(/^\//, '') : null))
          .filter(Boolean);
        if (list && list.length) setHeroImages(list);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // SEO: page titles, descriptions, canonical, and JSON-LD (FAQ)
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const siteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin || '').replace(
      /\/$/,
      '',
    );
    const pathPart = window.location.pathname || '/';
    const titles = Object.fromEntries(Object.entries(seo).map(([k, v]) => [k, v.title]));
    const descs = Object.fromEntries(Object.entries(seo).map(([k, v]) => [k, v.description]));
    document.title = titles[route] || titles.home;
    const ensureMeta = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else if (attrs.content) {
        el.setAttribute('content', attrs.content);
      }
      return el;
    };
    ensureMeta('meta[name="description"]', {
      name: 'description',
      content: descs[route] || descs.home,
    });
    ensureMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: titles[route] || titles.home,
    });
    ensureMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: descs[route] || descs.home,
    });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: siteUrl + pathPart });
    ensureMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', siteUrl + pathPart);

    // FAQPage JSON-LD on FAQ route
    const existingFaq = document.head.querySelector('script[data-faq-schema]');
    if (route !== 'faq') {
      if (existingFaq) existingFaq.remove();
      return;
    }
    try {
      const faqs = require('./content/faq.json').items || [];
      const data = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.slice(0, 20).map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      };
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-faq-schema', 'true');
      s.text = JSON.stringify(data).replace(/<\/script>/gi, '<\\/script>');
      if (existingFaq) existingFaq.replaceWith(s);
      else document.head.appendChild(s);
    } catch {}
  }, [route]);

  return (
    <div className="site">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {announcement.enabled && announcement.text && (
        <div className="announcement-bar" role="banner">
          <span>{announcement.text}</span>
          {announcement.link && announcement.linkText && (
            <a
              href={/^(https?:\/\/|\/)/.test(announcement.link) ? announcement.link : '#'}
              className="announcement-link"
            >
              {announcement.linkText}
            </a>
          )}
        </div>
      )}

      <header className="nav">
        <div className="container nav-inner">
          <a
            href="/"
            className="brand"
            onClick={(e) => {
              closeNav();
              e.preventDefault();
              navigate('/');
            }}
          >
            <img src={logoUrl} alt={`${site.name} logo`} className="brand-logo" />
            <span className="brand-name">{site.name}</span>
          </a>
          <button
            className={`nav-toggle ${navOpen ? 'open' : ''}`}
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
          <nav className={`nav-links ${navOpen ? 'show' : ''}`} aria-label="Primary">
            <a
              href="/"
              onClick={(e) => {
                closeNav();
                e.preventDefault();
                navigate('/');
              }}
            >
              Home
            </a>
            <a
              href="/#programs"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('#programs');
              }}
            >
              Programs
            </a>
            <a
              href="/schedule"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('/schedule');
              }}
            >
              Schedule
            </a>
            <a
              href="/coaches"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('/coaches');
              }}
            >
              Coaches
            </a>
            <a
              href="/gallery"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('/gallery');
              }}
            >
              Gallery
            </a>
            <a
              href="/faq"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('/faq');
              }}
            >
              FAQ
            </a>
            <a
              href="/#pricing"
              onClick={(e) => {
                e.preventDefault();
                closeNav();
                navigate('#pricing');
              }}
            >
              Pricing
            </a>
            <a
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-accent"
              onClick={() => {
                closeNav();
                try {
                  window.plausible &&
                    window.plausible('trial_signup_click', { props: { loc: 'nav' } });
                } catch {}
              }}
            >
              Free Trial
            </a>
          </nav>
        </div>
      </header>
      {!isCoachesPage && !isSchedulePage && !isGalleryPage && !isFaqPage && (
        <main id="main">
          {/* Hero */}
          <section id="home" className="hero">
            <div className="container hero-grid">
              <div className="hero-copy">
                <h1>{hero.headline}</h1>
                <h2>{hero.subheadline}</h2>
                <p className="hero-sub">{hero.body}</p>
                <ul className="checks">
                  {(hero.bullets || []).map((b) => <li key={b}>{b}</li>)}
                </ul>
                <div className="hero-ctas">
                  <a
                    href={SIGNUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-accent"
                    onClick={() => {
                      try {
                        window.plausible &&
                          window.plausible('trial_signup_click', { props: { loc: 'hero' } });
                      } catch {}
                    }}
                  >
                    {hero.ctaPrimary || 'Start Free Trial'}
                  </a>
                  <a href="#programs" className="btn btn-outline">
                    {hero.ctaSecondary || 'Explore Programs'}
                  </a>
                </div>
              </div>
              <div className="hero-media">
                <Carousel images={heroImages.length ? heroImages : ['img_3.png']} />
              </div>
            </div>
          </section>

          {/* Programs */}
          <section id="programs" className="section">
            <div className="container">
              <h2 className="section-title">Programs</h2>
              <div className="cards">
                {programs.map((p, i) => (
                  <motion.article
                    key={p.title}
                    className="card"
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.05 + i * 0.05 }}
                  >
                    <h3>{p.title}</h3>
                    <p>{p.description}</p>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          {/* Schedule moved to its own page */}

          {/* Coaches section moved to dedicated page */}

          {/* Pricing */}
          <section id="pricing" className="section section-alt">
            <div className="container">
              <h2 className="section-title">Pricing</h2>
              <div className="pricing">
                {pricing.map((plan) => (
                  <article key={plan.plan} className="price">
                    <h3>{plan.plan}</h3>
                    <p className="amount">{plan.amount}</p>
                    <ul>
                      {plan.features.map((f) => (
                        <li key={f}>&#10003; {f}</li>
                      ))}
                    </ul>
                    <a
                      href={SIGNUP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-accent"
                    >
                      Enroll
                    </a>
                  </article>
                ))}
              </div>
              <p className="fine" style={{ marginTop: 10 }}>
                Looking for drop-ins or private lessons? <a href="#contact">Contact us</a> for
                availability and details.
              </p>
            </div>
          </section>

          {/* Testimonials */}
          <section id="testimonials" className="section">
            <div className="container">
              <h2 className="section-title">What Students Say</h2>
              <ManualReviews />
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="section section-alt">
            <div className="container contact-grid">
              <div className="contact-info">
                <h2 className="section-title">Contact</h2>
                <p>{site.contactBlurb}</p>
                <ul className="contact-list">
                  <li>
                    <strong>Address:</strong>{' '}
                    <a href={MAPS_URL} target="_blank" rel="noopener noreferrer">
                      {ADDRESS}
                    </a>
                  </li>
                  <li>
                    <strong>Phone:</strong>{' '}
                    <a
                      href={`tel:${site.phone}`}
                      onClick={() => {
                        try {
                          window.plausible && window.plausible('phone_click');
                        } catch {}
                      }}
                    >
                      {site.phoneDisplay || site.phone}
                    </a>
                  </li>
                </ul>
                <div className="map-embed">
                  <iframe
                    title="Cavassani BJJ Map"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`}
                    width="100%"
                    style={{ border: 0, height: '100%' }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                {/* Removed separate call button; phone above is now clickable */}
              </div>
              <form
                className="contact-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (submitting) return;
                  setSubmitError('');
                  setSubmitting(true);
                  const form = e.currentTarget;
                  const data = new FormData(form);
                  const payload = {
                    name: data.get('name'),
                    email: data.get('email'),
                    message: data.get('message'),
                    'bot-field': data.get('bot-field'),
                    'h-captcha-response': captchaToken,
                  };
                  try {
                    const res = await fetch('/api/contact', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload),
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err?.error || `HTTP ${res.status}`);
                    }
                    try { window.plausible && window.plausible('contact_submit'); } catch {}
                    form.reset();
                    setCaptchaToken('');
                    try { window.hcaptcha && window.hcaptcha.reset(); } catch {}
                    setThankYouOpen(true);
                  } catch (err) {
                    setSubmitError(err?.message || 'Failed to send. Please try again.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {/* Honeypot — visually hidden; real users never fill this; bots do */}
                <input
                  type="text"
                  name="bot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                />
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <input id="name" name="name" type="text" placeholder="Your full name" required maxLength={100} />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" placeholder="you@example.com" required maxLength={254} pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$" title="Enter a valid email address like name@example.com" />
                </div>
                <div className="field field-grow">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" rows="4" placeholder="Tell us your goals" maxLength={2000} />
                </div>
                <div className="field captcha-field">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>Security check</label>
                  <div className="captcha-scaler">
                    <div
                      className="h-captcha"
                      data-sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                      data-theme="dark"
                      data-callback="onHCaptchaVerified"
                      data-expired-callback="onHCaptchaExpired"
                    />
                  </div>
                </div>
                {submitError && (
                  <p role="alert" style={{ color: 'var(--clr-accent, #c00)', marginBottom: 8, fontSize: '0.9rem' }}>
                    {submitError}
                  </p>
                )}
                <button type="submit" className="btn btn-accent full" disabled={submitting || !captchaToken} aria-busy={submitting}>
                  {submitting ? 'Sending…' : 'Request Free Trial'}
                </button>
              </form>
              {thankYouOpen && (
                <div
                  className="lightbox-backdrop"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Thank you"
                >
                  <div
                    className="lightbox-panel modal-panel"
                    role="document"
                  >
                    <button className="lightbox-close" aria-label="Close" onClick={() => setThankYouOpen(false)}>x</button>
                    <h2 className="section-title">Thank you</h2>
                    <p>We received your request and will be in touch soon.</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      )}

      {isCoachesPage && <Coaches />}
      {isSchedulePage && <Schedule />}
      {isGalleryPage && <Gallery />}
      {isFaqPage && <FAQ />}

      <footer className="footer">
        <div className="container footer-grid">
          <div className="foot-brand">
            <img src={logoUrl} alt={site.name} />
            <ul
              className="foot-links"
              style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'grid', gap: 6 }}
            >
              <li>
                <a href="#/schedule">Schedule</a>
              </li>
              <li>
                <a href="#/coaches">Coaches</a>
              </li>
              <li>
                <a href="#/gallery">Gallery</a>
              </li>
              <li>
                <a href="#/faq">FAQ</a>
              </li>
            </ul>
          </div>
          <div>
            <h4>Visit</h4>
            <p>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  try {
                    window.plausible && window.plausible('directions_click');
                  } catch {}
                }}
              >
                {ADDRESS}
              </a>
            </p>
          </div>
          <div>
            <h4>Contact</h4>
            <p>
              <a
                href={`tel:${site.phone}`}
                onClick={() => {
                  try {
                    window.plausible && window.plausible('phone_click');
                  } catch {}
                }}
              >
                {site.phoneDisplay || site.phone}
              </a>
            </p>
          </div>
          <div>
            <h4>Hours</h4>
            <ul className="hours">
              {site.hours.map((h) => (
                <li key={h.day}>
                  {h.day}: {h.text}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Social</h4>
            <p className="socials">
              <a
                href={site.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="social"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
                  <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm0 2h10a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3zm11 1a1 1 0 100 2 1 1 0 000-2zM12 7a5 5 0 100 10 5 5 0 000-10z" />
                </svg>
              </a>
              <a
                href={site.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="social"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
                  <path d="M22 12a10 10 0 10-11.5 9.9v-7h-2v-2.9h2v-2.2c0-2 1.2-3.2 3-3.2.9 0 1.8.1 1.8.1v2h-1c-1 0-1.3.6-1.3 1.2v1.9h2.3L15 14.9h-1.8v7A10 10 0 0022 12z" />
                </svg>
              </a>
            </p>
          </div>
        </div>
        <div className="container foot-meta">
          <small>
            © {new Date().getFullYear()} {site.name}
          </small>
        </div>
      </footer>
    </div>
  );
}

export default App;
